import { Flex, Text } from "styles/utility-components";

import Alert from "components/ui/alert/Alert";
import Button from "components/ui/button/Button";
import Modal from "components/ui/modal/Modal";
import type { McpClientWithSecret } from "lib/hooks/api/auth/mcp/schemas";
import {
  type ConnectionField,
  ConnectionFields,
  OAUTH_ENDPOINT_FIELDS,
  SERVER_FIELDS,
} from "./ConnectionFields";

type Props = {
  credential: McpClientWithSecret;
  onClose: () => void;
  /** Rotation replaces a secret that is already in use, which is worth saying out loud. */
  isRotation?: boolean;
};

const TITLE_ID = "mcp-credential-secret-modal-title";
const BODY_ID = "mcp-credential-secret-modal-body";

/**
 * Shows a newly issued client ID and secret. Secrets are stored hashed, so this is the only time
 * the value is readable — closing without copying it means rotating to get another.
 *
 * A new credential comes with everything else the AI tool's form asks for, so the admin can finish
 * setup from here. A rotation only changes the secret, so it shows the credential alone.
 */
export function CredentialSecretModal({ credential, onClose, isRotation = false }: Props) {
  const credentialFields: ConnectionField[] = [
    { label: "Client ID", value: credential.client_id, wrapValue: true },
    { label: "Client secret", value: credential.client_secret, wrapValue: true },
  ];

  return (
    <Modal
      open
      onOpenChange={onClose}
      maxWidth="560px"
      showCloseButton={false}
      aria-describedby={BODY_ID}
      padding={24}
    >
      <Flex column gap={24}>
        <Flex column gap={8}>
          <Text h2 id={TITLE_ID}>
            {isRotation ? "New client secret" : "Your connection details"}
          </Text>
          <Text id={BODY_ID} description>
            {isRotation
              ? "The previous secret has stopped working. Paste this one into your AI tool to reconnect it."
              : "Paste these into your AI tool to connect it to Stotles. Your tool may not ask for all of them."}
          </Text>
        </Flex>

        <Alert type="warning">
          Copy the secret now — you won't be able to see it again. If you lose it, you can generate
          a replacement from this page.
        </Alert>

        {/* Named by the heading, so the group says what it holds in both cases. */}
        <Flex column gap={24} role="group" aria-labelledby={TITLE_ID}>
          {isRotation ? (
            <ConnectionFields fields={credentialFields} />
          ) : (
            <>
              <Flex column gap={12}>
                <Text h3>Server</Text>
                <ConnectionFields fields={SERVER_FIELDS} />
              </Flex>
              <Flex column gap={12}>
                <Text h3>Authentication</Text>
                <ConnectionFields fields={[...credentialFields, ...OAUTH_ENDPOINT_FIELDS]} />
              </Flex>
            </>
          )}
        </Flex>

        <Flex justifyContent="flex-end">
          <Button onClick={onClose}>Done</Button>
        </Flex>
      </Flex>
    </Modal>
  );
}
